<script setup lang="ts">
const { openSettings } = useCookieConsent()

definePageMeta({
  layout: 'front-page',
  public: true,
  pageTitle: {
    title: 'Cookie Policy',
    subtitle: 'Hoe wij cookies gebruiken en hoe u deze kunt beheren',
    icon: 'bx-cookie',
    iconColor: 'warning',
  },
})

const sections = [
  {
    title: '1. Wat zijn cookies?',
    content: 'Cookies zijn kleine tekstbestanden die op uw computer of mobiel apparaat worden geplaatst wanneer u onze website bezoekt. Cookies worden veel gebruikt om websites te laten werken of efficiënter te laten werken, en om informatie te verstrekken aan de eigenaren van de site.',
  },
  {
    title: '2. Waarom gebruiken wij cookies?',
    content: `Wij gebruiken cookies voor verschillende doeleinden:
• Om onze website goed te laten functioneren (essentiële cookies)
• Om uw voorkeuren te onthouden (functionele cookies)
• Om de prestaties van onze website te analyseren (analytische cookies)
• Om u relevante advertenties te tonen (marketing cookies)
• Om de beveiliging te waarborgen (beveiligingscookies)`,
  },
  {
    title: '3. Welke cookies gebruiken wij?',
    content: 'Onze website gebruikt de volgende soorten cookies:',
  },
]

const cookieTypes = [
  {
    category: 'Strikt noodzakelijke cookies',
    description: 'Deze cookies zijn essentieel voor het functioneren van de website. Zonder deze cookies kan de website niet naar behoren functioneren.',
    cookies: [
      { name: 'sb-*-auth-token', purpose: 'Supabase authenticatie - Houdt uw inlogsessie bij', duration: 'Sessie/1 jaar' },
      { name: 'cookie_consent', purpose: 'Slaat uw cookie voorkeuren op', duration: '1 jaar' },
      { name: '__vercel_live_token', purpose: 'Vercel hosting - Sessietoken voor website delivery', duration: 'Sessie' },
    ],
  },
  {
    category: 'Functionele cookies',
    description: 'Deze cookies maken het mogelijk om verbeterde functionaliteit en personalisatie te bieden.',
    cookies: [
      { name: 'theme_preference', purpose: 'Onthoudt uw thema voorkeur (licht/donker)', duration: '1 jaar' },
      { name: 'checkout_session', purpose: 'Stripe - Houdt uw winkelwagen/checkout sessie bij', duration: 'Sessie' },
    ],
  },
  {
    category: 'Analytische cookies',
    description: 'Wij gebruiken Umami Analytics, een privacy-vriendelijke analytische dienst die geen persoonlijke gegevens verzamelt en volledig AVG-conform is.',
    cookies: [
      { name: 'umami.uuid', purpose: 'Umami Analytics - Anonieme bezoekersidentificatie (geen persoonlijke data)', duration: 'Sessie' },
    ],
  },
]

const managementSections = [
  {
    title: '4. Cookies beheren',
    content: 'U kunt uw cookie voorkeuren op elk moment wijzigen via onze cookie banner of via de instellingen van uw browser. Let op: als u cookies uitschakelt, kunnen sommige functies van onze website mogelijk niet goed werken.',
  },
  {
    title: '5. Cookies uitschakelen in uw browser',
    content: `De meeste browsers accepteren cookies automatisch. U kunt uw browserinstellingen aanpassen om cookies te weigeren of om een melding te ontvangen wanneer een cookie wordt verstuurd. Hier vindt u instructies voor de meest gebruikte browsers:

• Google Chrome: Menu > Instellingen > Privacy en beveiliging > Cookies en andere sitegegevens
• Mozilla Firefox: Menu > Opties > Privacy & Beveiliging > Cookies en sitegegevens
• Safari: Voorkeuren > Privacy > Cookies en websitegegevens
• Microsoft Edge: Menu > Instellingen > Cookies en sitemachtigingen > Cookies en sitegegevens
• Opera: Menu > Instellingen > Privacy en beveiliging > Cookies`,
  },
  {
    title: '6. Third-party cookies',
    content: `Onze website maakt gebruik van enkele externe diensten die cookies kunnen plaatsen. Al deze diensten zijn geselecteerd op basis van privacy en AVG-compliance:

• Supabase (Frankfurt, Duitsland) - Database en authenticatie
• Stripe (EU) - Veilige betalingsverwerking
• Umami Cloud (EU) - Privacy-vriendelijke analytics (geen persoonlijke data)
• Vercel (Frankfurt, Duitsland) - Website hosting
• Brevo (Frankrijk) - E-mailverzending

Wij maken geen gebruik van tracking cookies van Google Analytics, Facebook of andere advertentienetwerken.`,
  },
  {
    title: '7. Cookie toestemming',
    content: `Bij uw eerste bezoek aan onze website wordt u gevraagd om toestemming te geven voor het gebruik van cookies. U kunt:
• Alle cookies accepteren
• Alleen noodzakelijke cookies accepteren
• Uw voorkeuren per categorie aanpassen

Uw keuze wordt opgeslagen en u kunt deze op elk moment wijzigen via de cookie-instellingen link in de footer van onze website.`,
  },
  {
    title: '8. Updates van dit cookie beleid',
    content: `Wij kunnen dit cookie beleid van tijd tot tijd bijwerken. De meest recente versie is altijd beschikbaar op deze pagina. De laatste wijziging was op ${new Date().toLocaleDateString('nl-BE')}.`,
  },
  {
    title: '9. Meer informatie',
    content: `Voor meer informatie over cookies en hoe u deze kunt beheren, kunt u de volgende websites bezoeken:
• www.aboutcookies.org
• www.allaboutcookies.org
• www.youronlinechoices.eu`,
  },
  {
    title: '10. Contact',
    content: `Als u vragen heeft over ons gebruik van cookies, neem dan contact met ons op:

't SpeelFabrik VZW
E-mail: contact@tspeelfabrik.be
Telefoon: +32 474 68 10 52
Adres: Langestraat 15b, 1910 Kampenhout`,
  },
]
</script>

<template>
  <VContainer>
    <VCard
      class="pa-4 mx-auto"
      max-width="1200"
    >
      <VCardText>
        <VContainer>
          <!-- Introduction sections -->
          <div
            v-for="(section, index) in sections"
            :key="index"
            class="mb-6"
          >
            <h2 class="text-h6 mb-3">
              {{ section.title }}
            </h2>
            <p
              class="text-body-1"
              style="white-space: pre-line;"
            >
              {{ section.content }}
            </p>
          </div>

          <!-- Cookie types table -->
          <div
            v-for="(type, index) in cookieTypes"
            :key="index"
            class="mb-8"
          >
            <h3 class="text-h6 mb-2">
              {{ type.category }}
            </h3>
            <p class="text-body-2 mb-4">
              {{ type.description }}
            </p>

            <VTable>
              <thead>
                <tr>
                  <th class="text-left">
                    Cookie naam
                  </th>
                  <th class="text-left">
                    Doel
                  </th>
                  <th class="text-left">
                    Duur
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(cookie, cookieIndex) in type.cookies"
                  :key="cookieIndex"
                >
                  <td><code>{{ cookie.name }}</code></td>
                  <td>{{ cookie.purpose }}</td>
                  <td>{{ cookie.duration }}</td>
                </tr>
              </tbody>
            </VTable>
          </div>

          <!-- Management sections -->
          <div
            v-for="(section, index) in managementSections"
            :key="`mgmt-${index}`"
            class="mb-6"
          >
            <h2 class="text-h6 mb-3">
              {{ section.title }}
            </h2>
            <p
              class="text-body-1"
              style="white-space: pre-line;"
            >
              {{ section.content }}
            </p>
          </div>

          <VDivider class="my-6" />

          <div class="text-center">
            <VBtn
              to="/"
              color="primary"
              class="me-3"
            >
              Terug naar Home
            </VBtn>
            <VBtn
              variant="outlined"
              color="primary"
              @click="openSettings"
            >
              Cookie Instellingen
            </VBtn>
          </div>
        </VContainer>
      </VCardText>
    </VCard>
  </VContainer>
</template>

<style lang="scss" scoped>
code {
  border-radius: 4px;
  background-color: rgba(var(--v-theme-on-surface), 0.05);
  font-family: monospace;
  padding-block: 2px;
  padding-inline: 6px;
}
</style>
